/**
 * CallNotesEditor Component
 *
 * Displays AI-generated call notes with three tabs:
 * - Attributes: AI-suggested ticket fields (subject, priority, type, category, status)
 * - Notes: Editable formatted notes
 * - Transcript: Full call transcript
 */

import { useState, useEffect } from 'react'
import { Send, Copy, RefreshCw, CheckCircle2, Loader2, AlertCircle, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useCallNotes, useTranscript, useTicketAttributeSuggestions,
  updateCallNotes, addNotesToTicket,
} from '@/hooks/useCallRecordings'

interface CallNotesEditorProps {
  recordingId: string
  ticketId?: number
  onAddedToTicket?: () => void
}

export function CallNotesEditor({ recordingId, ticketId, onAddedToTicket }: CallNotesEditorProps) {
  const { data: notes, isLoading: notesLoading } = useCallNotes(recordingId)
  const { data: transcript, isLoading: transcriptLoading } = useTranscript(recordingId)
  const { data: suggestions, isLoading: suggestionsLoading } = useTicketAttributeSuggestions(recordingId, ticketId)

  // Notes state
  const [editedNote, setEditedNote] = useState('')
  const [noteType, setNoteType] = useState<'internal' | 'public'>('internal')
  const [includeTranscript, setIncludeTranscript] = useState(false)
  const [hasEdited, setHasEdited] = useState(false)
  const [activeTab, setActiveTab] = useState('attributes')

  // Attribute state
  const [ticketSubject, setTicketSubject] = useState('')
  // Whether to apply the AI-suggested summary. Defaults ON (pre-filled), but the
  // user can decline in one click to keep the ticket's current summary.
  const [applySummary, setApplySummary] = useState(true)
  const [selectedPriorityId, setSelectedPriorityId] = useState('')
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState('')
  const [selectedStatusId, setSelectedStatusId] = useState('')
  const [selectedSubcategory, setSelectedSubcategory] = useState('')
  const [selectedImpactLevel, setSelectedImpactLevel] = useState('')
  const [selectedUrgencyLevel, setSelectedUrgencyLevel] = useState('')
  const [includeTimeEntry, setIncludeTimeEntry] = useState(false)
  const [timeHours, setTimeHours] = useState('')

  // Action state
  const [isSaving, setIsSaving] = useState(false)
  const [isAddingToTicket, setIsAddingToTicket] = useState(false)
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Initialize notes text
  useEffect(() => {
    if (notes?.formattedNote && !hasEdited) {
      setEditedNote(notes.formattedNote)
    }
  }, [notes, hasEdited])

  // Initialize attribute selections from AI suggestions
  // Priority and impact/urgency default to empty ("keep current") — shown as hints only
  // Status auto-selects from AI recommendation so user doesn't have to pick manually
  useEffect(() => {
    if (suggestions) {
      if (!ticketSubject) setTicketSubject(suggestions.suggestedSubject || '')
      if (!selectedTicketTypeId && suggestions.suggestedTicketTypeId)
        setSelectedTicketTypeId(String(suggestions.suggestedTicketTypeId))
      if (!selectedCategoryId && suggestions.suggestedCategoryId)
        setSelectedCategoryId(String(suggestions.suggestedCategoryId))
      if (!selectedSubcategory && suggestions.suggestedSubcategory)
        setSelectedSubcategory(suggestions.suggestedSubcategory)
      if (!selectedStatusId && suggestions.suggestedStatusId)
        setSelectedStatusId(String(suggestions.suggestedStatusId))
      if (!timeHours && suggestions.suggestedTimeHours)
        setTimeHours(String(suggestions.suggestedTimeHours))
    }
  }, [suggestions])

  const handleNoteChange = (value: string) => {
    setEditedNote(value)
    setHasEdited(true)
  }

  const handleSaveEdits = async () => {
    try {
      setIsSaving(true)
      await updateCallNotes(recordingId, editedNote)
      setActionMessage({ type: 'success', text: 'Notes saved' })
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to save notes' })
    } finally {
      setIsSaving(false)
      setTimeout(() => setActionMessage(null), 3000)
    }
  }

  const handleAddToTicket = async () => {
    if (!ticketId) {
      setActionMessage({ type: 'error', text: 'No ticket selected' })
      setTimeout(() => setActionMessage(null), 3000)
      return
    }
    try {
      setIsAddingToTicket(true)
      if (hasEdited) await updateCallNotes(recordingId, editedNote)

      // Build ticket updates from attribute selections
      const ticketUpdates: Record<string, unknown> = {}
      if (applySummary && ticketSubject) ticketUpdates.summary = ticketSubject
      if (selectedPriorityId) ticketUpdates.priority_id = Number(selectedPriorityId)
      if (selectedStatusId) ticketUpdates.status_id = Number(selectedStatusId)
      if (selectedTicketTypeId) ticketUpdates.tickettype_id = Number(selectedTicketTypeId)
      if (selectedCategoryId) {
        const cat = suggestions?.availableCategories.find(c => c.id === Number(selectedCategoryId))
        if (cat) ticketUpdates.category_1 = cat.name
      }
      if (selectedSubcategory.trim()) ticketUpdates.category_2 = selectedSubcategory.trim()
      if (selectedImpactLevel) ticketUpdates.impactlevel = Number(selectedImpactLevel)
      if (selectedUrgencyLevel) ticketUpdates.urgencylevel = Number(selectedUrgencyLevel)

      const result = await addNotesToTicket(recordingId, {
        ticketId,
        noteText: editedNote,
        noteType,
        includeTranscript,
        ticketUpdates: Object.keys(ticketUpdates).length > 0 ? ticketUpdates as any : undefined,
        timeEntry: includeTimeEntry && Number(timeHours) > 0
          ? { hours: Number(timeHours), notes: ticketSubject ? `${ticketSubject} - call notes session` : undefined }
          : undefined,
      }) as Record<string, unknown>
      const attachErr = result?.attachmentError as string | null
      if (attachErr) {
        setActionMessage({ type: 'error', text: `Notes added but attachment failed: ${attachErr.slice(0, 200)}` })
      } else {
        setActionMessage({ type: 'success', text: `Notes added to ticket #${ticketId}${result?.attachmentId ? ' (with recording)' : ''}` })
      }
      onAddedToTicket?.()
    } catch {
      setActionMessage({ type: 'error', text: 'Failed to add notes to ticket' })
    } finally {
      setIsAddingToTicket(false)
      setTimeout(() => setActionMessage(null), 3000)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedNote)
    setActionMessage({ type: 'success', text: 'Copied to clipboard' })
    setTimeout(() => setActionMessage(null), 2000)
  }

  const handleResetToOriginal = () => {
    if (notes?.formattedNote) { setEditedNote(notes.formattedNote); setHasEdited(false) }
  }

  if (notesLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-32 w-full bg-gray-200 rounded animate-pulse" />
        <div className="h-10 w-full bg-gray-200 rounded animate-pulse" />
      </div>
    )
  }

  if (!notes) {
    return <div className="text-center py-8 text-gray-500"><p>Notes not available yet. Please wait for processing to complete.</p></div>
  }

  const sentimentColors: Record<string, string> = {
    satisfied: 'bg-green-100 text-green-800', neutral: 'bg-gray-100 text-gray-800',
    frustrated: 'bg-orange-100 text-orange-800', concerned: 'bg-yellow-100 text-yellow-800',
  }

  return (
    <div className="space-y-4">
      {actionMessage && (
        <div className={`flex items-center gap-2 p-2 rounded text-sm ${actionMessage.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {actionMessage.type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {actionMessage.text}
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">AI Summary</label>
          <span className={`text-xs px-2 py-0.5 rounded-full ${sentimentColors[notes.customerSentiment] || sentimentColors.neutral}`}>{notes.customerSentiment}</span>
        </div>
        <p className="text-sm text-gray-600">{notes.summary}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="attributes" className="text-xs">Attributes</TabsTrigger>
          <TabsTrigger value="notes" className="text-xs">Notes</TabsTrigger>
          <TabsTrigger value="transcript" className="text-xs">Transcript</TabsTrigger>
        </TabsList>

        {/* Attributes Tab */}
        <TabsContent value="attributes" className="space-y-4">
          {suggestionsLoading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Analyzing transcript for ticket attributes...</span>
            </div>
          ) : suggestions ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-3 py-2 rounded">
                <Sparkles className="h-3.5 w-3.5" />
                AI-suggested values - review and edit before applying
              </div>

              {/* Subject */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Ticket Subject</label>
                <Input
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of the issue"
                  maxLength={100}
                  disabled={!applySummary}
                  className={applySummary ? undefined : 'opacity-50'}
                />
                <p className="text-xs text-gray-400">{ticketSubject.length}/100 characters</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applySummary}
                    onChange={(e) => setApplySummary(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-xs text-gray-600">
                    Apply this summary to the ticket
                    {!applySummary && <span className="text-amber-600"> — keeping current summary</span>}
                  </span>
                </label>
              </div>

              {/* Priority */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Priority</label>
                <select
                  value={selectedPriorityId}
                  onChange={(e) => setSelectedPriorityId(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{suggestions.currentTicket?.priorityName ? `Current: ${suggestions.currentTicket.priorityName}` : 'Keep current priority'}</option>
                  {suggestions.availablePriorities.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {suggestions.suggestedPriorityName && (
                  <p className="text-xs text-gray-500 italic">
                    AI suggestion: {suggestions.suggestedPriorityName}{suggestions.priorityReason ? ` — ${suggestions.priorityReason}` : ''}
                  </p>
                )}
              </div>

              {/* Ticket Type */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Ticket Type</label>
                <select
                  value={selectedTicketTypeId}
                  onChange={(e) => setSelectedTicketTypeId(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={suggestions.availableTicketTypes.length === 0}
                >
                  {suggestions.availableTicketTypes.length === 0 ? (
                    <option value="">No types available</option>
                  ) : (
                    <>
                      <option value="">-- Select Type --</option>
                      {suggestions.availableTicketTypes.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Category */}
              {suggestions.availableCategories.length > 0 && (
                <div className="space-y-1">
                  <label className="text-sm font-medium">Category</label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select Category --</option>
                    {suggestions.availableCategories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Subcategory */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Subcategory</label>
                {(() => {
                  const filteredSubcats = (suggestions.availableSubcategories || [])
                    .filter(sc => !selectedCategoryId || sc.parentId === Number(selectedCategoryId));
                  return filteredSubcats.length > 0 ? (
                    <select
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select Subcategory --</option>
                      {filteredSubcats.map(sc => (
                        <option key={sc.id} value={sc.name}>{sc.name}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={selectedSubcategory}
                      onChange={(e) => setSelectedSubcategory(e.target.value)}
                      placeholder="Optional subcategory"
                    />
                  );
                })()}
                {suggestions.suggestedSubcategory && !selectedSubcategory && (
                  <p className="text-xs text-gray-500 italic">
                    AI suggestion: {suggestions.suggestedSubcategory}
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Status</label>
                <select
                  value={selectedStatusId}
                  onChange={(e) => setSelectedStatusId(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{suggestions.currentTicket?.statusName ? `Current: ${suggestions.currentTicket.statusName}` : 'Keep current status'}</option>
                  {suggestions.availableStatuses.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {suggestions.suggestedStatusName && (
                  <p className="text-xs text-gray-500 italic">
                    AI suggestion: {suggestions.suggestedStatusName}
                  </p>
                )}
              </div>

              {/* Impact */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Impact</label>
                <select
                  value={selectedImpactLevel}
                  onChange={(e) => setSelectedImpactLevel(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{suggestions.currentTicket?.impactLevel ? `Current: ${['', 'Low', 'Medium', 'High', 'Critical'][suggestions.currentTicket.impactLevel] || suggestions.currentTicket.impactLevel}` : 'Keep current impact'}</option>
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                  <option value="4">Critical</option>
                </select>
                {suggestions.suggestedImpactLevel && (
                  <p className="text-xs text-gray-500 italic">
                    AI suggestion: {['', 'Low', 'Medium', 'High', 'Critical'][suggestions.suggestedImpactLevel] || suggestions.suggestedImpactLevel}
                  </p>
                )}
              </div>

              {/* Urgency */}
              <div className="space-y-1">
                <label className="text-sm font-medium">Urgency</label>
                <select
                  value={selectedUrgencyLevel}
                  onChange={(e) => setSelectedUrgencyLevel(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{suggestions.currentTicket?.urgencyLevel ? `Current: ${['', 'Low', 'Medium', 'High', 'Critical'][suggestions.currentTicket.urgencyLevel] || suggestions.currentTicket.urgencyLevel}` : 'Keep current urgency'}</option>
                  <option value="1">Low</option>
                  <option value="2">Medium</option>
                  <option value="3">High</option>
                  <option value="4">Critical</option>
                </select>
                {suggestions.suggestedUrgencyLevel && (
                  <p className="text-xs text-gray-500 italic">
                    AI suggestion: {['', 'Low', 'Medium', 'High', 'Critical'][suggestions.suggestedUrgencyLevel] || suggestions.suggestedUrgencyLevel}
                  </p>
                )}
              </div>

              {/* Time Entry */}
              <div className="space-y-2 pt-2 border-t">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTimeEntry}
                    onChange={(e) => setIncludeTimeEntry(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Log time entry</span>
                </label>
                {includeTimeEntry && (
                  <div className="flex items-center gap-2 pl-6">
                    <Input
                      type="number"
                      value={timeHours}
                      onChange={(e) => setTimeHours(e.target.value)}
                      step="0.25"
                      min="0.25"
                      className="w-24"
                    />
                    <span className="text-sm text-gray-500">hours</span>
                    <span className="text-xs text-gray-400">
                      (call: {suggestions.callDurationMinutes} min)
                    </span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8 text-sm">
              Ticket attribute suggestions not available
            </p>
          )}
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Formatted Note</label>
              <div className="flex gap-2">
                {hasEdited && <Button variant="ghost" size="sm" onClick={handleResetToOriginal}><RefreshCw className="h-4 w-4 mr-1" />Reset</Button>}
                <Button variant="ghost" size="sm" onClick={handleCopy}><Copy className="h-4 w-4 mr-1" />Copy</Button>
              </div>
            </div>
            <Textarea value={editedNote} onChange={(e) => handleNoteChange(e.target.value)} className="min-h-[200px] font-mono text-sm" placeholder="Edit the AI-generated notes here..." />
            {hasEdited && (
              <Button size="sm" onClick={handleSaveEdits} disabled={isSaving} style={{ backgroundColor: '#16a34a', color: 'white' }} className="hover:opacity-90">
                {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save Edits
              </Button>
            )}
          </div>
          <div className="space-y-3">
            <label className="text-sm font-medium">Note Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="noteType" value="internal" checked={noteType === 'internal'} onChange={() => setNoteType('internal')} />
                <span className="text-sm">Internal Note</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="noteType" value="public" checked={noteType === 'public'} onChange={() => setNoteType('public')} />
                <span className="text-sm">Public Note</span>
              </label>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={includeTranscript} onChange={(e) => setIncludeTranscript(e.target.checked)} className="rounded" />
            <span className="text-sm">Include full transcript in note</span>
          </label>
        </TabsContent>

        {/* Transcript Tab */}
        <TabsContent value="transcript" className="space-y-4">
          {transcriptLoading ? (
            <div className="h-48 w-full bg-gray-200 rounded animate-pulse" />
          ) : transcript ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Full Transcript</label>
                <span className="text-xs text-gray-400">{transcript.wordCount} words</span>
              </div>
              <div className="max-h-[300px] overflow-y-auto rounded-md border p-4 bg-gray-50">
                <p className="text-sm whitespace-pre-wrap">{transcript.text}</p>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Transcript not available</p>
          )}
        </TabsContent>
      </Tabs>

      {/* Action button */}
      {ticketId && !notes.addedToTicket && (
        <div className="pt-4 border-t">
          <Button onClick={handleAddToTicket} disabled={isAddingToTicket} style={{ backgroundColor: '#2563eb', color: 'white' }} className="w-full gap-2 hover:opacity-90">
            {isAddingToTicket ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Update Ticket #{ticketId}
          </Button>
        </div>
      )}
      {!ticketId && (
        <div className="text-sm text-amber-600 pt-4 border-t">No ticket associated with this recording. Copy the notes and add them manually.</div>
      )}
      {notes.addedToTicket && (
        <div className="flex items-center gap-2 text-green-600 pt-4 border-t">
          <CheckCircle2 className="h-5 w-5" /><span className="text-sm">Notes already added to ticket</span>
        </div>
      )}
    </div>
  )
}
